import { useState, useRef, useCallback, forwardRef, useImperativeHandle, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Save, Loader2, Upload, X, Building2, MapPin, Landmark, ImageIcon, TrendingUp, FileBarChart, LayoutDashboard, Percent, RotateCcw, BarChart2, ShieldCheck, Clock, Calculator, Target, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { useCompanyDetails } from '@/hooks/use-company-details';
import { compressLogoImage } from '@/lib/compress-image';
import { appendAuditEntry } from '@/lib/audit-log-storage';
import { useDataMode } from '@/contexts/DataModeContext';
import { apiFetch } from '@/lib/api-error-handler';
import { getApiBaseUrl } from '@/lib/data-service';
import { useAuth } from '@/providers/AuthProvider';
import { SettingsLayout } from '@/components/Pages/SettingsLayout';
import { usePermissions } from '@/contexts/PermissionsContext';
import { useDashboardIntegrationStatus } from '@/hooks/use-dashboard-integration-status';
import { useFinanceSettings } from '@/hooks/use-finance-settings';
import { useUtilisationFormula } from '@/hooks/use-utilisation-formula';
import { useWizardMode } from '@/contexts/WizardModeContext';
import type { UIFormulaType } from '@/hooks/use-utilisation-formula';
import {
  GP_METHOD_LABELS,
  GP_METHOD_DESCRIPTIONS,
  type GpCalculationMethod,
} from '@/lib/gp-calculation';
import { type RevenueDisplayMode } from '@/lib/finance-settings-storage';

const GP_METHODS: GpCalculationMethod[] = ['simple', 'contribution', 'fully-loaded'];

const REVENUE_DISPLAY_MODES: {
  value: RevenueDisplayMode;
  label: string;
  description: string;
}[] = [
  {
    value: 'both',
    label: 'Both (Invoiced & Recognised)',
    description:
      'Show both Invoiced Revenue and Recognised Revenue rows in the P&L Report. Recommended for fixed-price projects with formal revenue recognition.',
  },
  {
    value: 'invoiced',
    label: 'Invoiced Revenue only',
    description:
      'Show only what has been billed to the client (actualSales). Keeps the P&L clean for T&M businesses that bill time as incurred.',
  },
  {
    value: 'recognised',
    label: 'Recognised Revenue only',
    description:
      'Show only the recognised revenue figure from the Revenue Recognition schedule. Invoiced Revenue is hidden. Recommended for businesses with formal IFRS 15 / ASC 606 recognition.',
  },
];

// ============================================================================
// Accordion Section — matches the Notifications page card style
// ============================================================================

interface AccordionSectionProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}

function AccordionSection({ icon, title, description, defaultOpen = true, children }: AccordionSectionProps) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-xl border border-border bg-card shadow-sm overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-3 px-4 py-4 hover:bg-accent/40 transition-colors text-left group"
        aria-expanded={open}
      >
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 group-hover:bg-primary/20 transition-colors">
          {icon}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold leading-snug">{title}</p>
          <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{description}</p>
        </div>
        <ChevronDown
          className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <div className="border-t border-border px-6 py-5">
          {children}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Section ref handle — exposed via forwardRef so the page can call save()
// ============================================================================

export interface SectionHandle {
  save: () => Promise<void>;
}


// ============================================================================
// Company Details Section
// ============================================================================

export interface CompanyImportData {
  name?: string;
  companyRegistrationNumber?: string;
  vatNumber?: string;
  phone?: string;
  email?: string;
  website?: string;
  registeredAddress?: {
    line1?: string;
    line2?: string;
    city?: string;
    county?: string;
    postcode?: string;
    country?: string;
  };
  bankDetails?: {
    bankName?: string;
    accountName?: string;
    sortCode?: string;
    accountNumber?: string;
    iban?: string;
    swiftBic?: string;
  };
}

interface CompanyDetailsSectionProps {
  importData?: CompanyImportData | null;

}

export const CompanyDetailsSection = forwardRef(
  function CompanyDetailsSection({ importData }: CompanyDetailsSectionProps, ref: React.Ref<SectionHandle>) {
  const {
    details,
    saveDetails,
    discardChanges,
    isSaving,
    hasUnsavedChanges,
    isLoaded,
    setField,
    setAddressField,
    setBankField,
    setLogoUrl,
  } = useCompanyDetails();
  const { isDemoMode } = useDataMode();
  const { authenticatedUser } = useAuth();
  const { financeStatus } = useDashboardIntegrationStatus();
  const isFinanceConnected = financeStatus.isConnected;

  const changedBy = isDemoMode
    ? 'Demo User'
    : authenticatedUser?.name ?? authenticatedUser?.email ?? 'Unknown User';

  // --- Finance import: pre-populate fields when importData is provided ---
  // Wait for isLoaded so that the local company details fetch has completed
  // before overlaying GraphQL data; otherwise setDetails(loaded) in the hook
  // can overwrite the GraphQL values.
  useEffect(() => {
    if (!importData || !isLoaded) return;
    if (importData.name) setField('name', importData.name);
    if (importData.companyRegistrationNumber) setField('companyRegistrationNumber', importData.companyRegistrationNumber);
    if (importData.vatNumber) setField('vatNumber', importData.vatNumber);
    if (importData.phone) setField('phone', importData.phone);
    if (importData.email) setField('email', importData.email);
    if (importData.website) setField('website', importData.website);
    if (importData.registeredAddress) {
      const a = importData.registeredAddress;
      if (a.line1) setAddressField('line1', a.line1);
      if (a.line2) setAddressField('line2', a.line2 ?? '');
      if (a.city) setAddressField('city', a.city);
      if (a.county) setAddressField('county', a.county ?? '');
      if (a.postcode) setAddressField('postcode', a.postcode);
      if (a.country) setAddressField('country', a.country);
    }
    if (importData.bankDetails) {
      const b = importData.bankDetails;
      if (b.bankName) setBankField('bankName', b.bankName);
      if (b.accountName) setBankField('accountName', b.accountName);
      if (b.sortCode) setBankField('sortCode', b.sortCode);
      if (b.accountNumber) setBankField('accountNumber', b.accountNumber);
      if (b.iban) setBankField('iban', b.iban);
      if (b.swiftBic) setBankField('swiftBic', b.swiftBic);
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [importData, isLoaded]);

  // --- Logo drag-and-drop ---
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isCompressing, setIsCompressing] = useState(false);

  const handleLogoFile = useCallback(async (file: File) => {
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file (PNG, JPG, SVG, WebP).');
      return;
    }
    setIsCompressing(true);
    try {
      const compressed = await compressLogoImage(file);
      setLogoUrl(compressed);
      toast.success('Logo uploaded successfully.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to process logo.');
    } finally {
      setIsCompressing(false);
    }
  }, [setLogoUrl]);

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleLogoFile(file);
  }, [handleLogoFile]);

  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleLogoFile(file);
    e.target.value = '';
  }, [handleLogoFile]);

  const handleRemoveLogo = useCallback(() => {
    setLogoUrl(undefined);
    toast.info('Logo removed.');
  }, [setLogoUrl]);

  // --- Bubble save/dirty state up to page ---
  const save = useCallback(async () => {
    if (isFinanceConnected) return;
    await saveDetails();
    appendAuditEntry({
      changedBy,
      category: 'Settings',
      action: 'Updated',
      entityName: 'Company Details',
      detail: 'Updated via Settings > General > Company Details',
    });
  }, [saveDetails, changedBy, isFinanceConnected]);

  useImperativeHandle(ref, () => ({ save }), [save]);

  return (
    <AccordionSection
      icon={<Building2 className="h-5 w-5 text-primary" />}
      title="Company Details"
      description="Your company information, address and bank details used on invoices"
    >

      <div className="space-y-8">

        {/* ── Logo Upload ─────────────────────────────────────────────── */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <ImageIcon className="h-4 w-4 text-muted-foreground" />
            <h3 className="text-sm font-semibold">Company Logo</h3>
          </div>

          {details.logoUrl ? (
            <div className="flex items-start gap-4">
              <div className="border rounded-lg p-3 bg-muted/20 flex items-center justify-center" style={{ minWidth: 160, minHeight: 60 }}>
                <img
                  src={details.logoUrl}
                  alt="Company logo"
                  className="max-h-[80px] max-w-[300px] object-contain"
                />
              </div>
              <div className="flex flex-col gap-2 pt-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isCompressing}
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Replace Logo
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleRemoveLogo}
                  className="text-destructive hover:text-destructive"
                >
                  <X className="h-4 w-4 mr-2" />
                  Remove
                </Button>
              </div>
            </div>
          ) : (
            <div
              className={`relative border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                isDragOver
                  ? 'border-primary bg-primary/5'
                  : 'border-muted-foreground/25 hover:border-primary/50 hover:bg-muted/30'
              }`}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
              onDragLeave={() => setIsDragOver(false)}
              onDrop={handleDrop}
            >
              {isCompressing ? (
                <div className="flex flex-col items-center gap-2">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">Processing image…</p>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2">
                  <Upload className="h-8 w-8 text-muted-foreground" />
                  <p className="text-sm font-medium">Click to upload or drag and drop</p>
                  <p className="text-xs text-muted-foreground">PNG, JPG, SVG, WebP · Max 10 MB · Scaled to 400×120 px</p>
                </div>
              )}
            </div>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleFileInputChange}
          />
        </div>

        <Separator />

        {/* ── Company Information ──────────────────────────────────────── */}
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Building2 className="h-4 w-4 text-muted-foreground" />
            <h3 className="text-sm font-semibold">Company Information</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="cd-name" className="flex items-center flex-wrap gap-y-1">
                Company Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="cd-name"
                value={details.name}
                onChange={(e) => setField('name', e.target.value)}
                placeholder="Acme Ltd"
                readOnly={isFinanceConnected}
                disabled={isFinanceConnected}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cd-reg" className="flex items-center flex-wrap gap-y-1">
                Company Registration Number
              </Label>
              <Input
                id="cd-reg"
                value={details.companyRegistrationNumber ?? ''}
                onChange={(e) => setField('companyRegistrationNumber', e.target.value)}
                placeholder="12345678"
                readOnly={isFinanceConnected}
                disabled={isFinanceConnected}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cd-vat" className="flex items-center flex-wrap gap-y-1">
                VAT Number
              </Label>
              <Input
                id="cd-vat"
                value={details.vatNumber ?? ''}
                onChange={(e) => setField('vatNumber', e.target.value)}
                placeholder="GB 123 4567 89"
                readOnly={isFinanceConnected}
                disabled={isFinanceConnected}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cd-phone" className="flex items-center flex-wrap gap-y-1">
                Phone
              </Label>
              <Input
                id="cd-phone"
                type="tel"
                value={details.phone ?? ''}
                onChange={(e) => setField('phone', e.target.value)}
                placeholder="+44 20 1234 5678"
                readOnly={isFinanceConnected}
                disabled={isFinanceConnected}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cd-email" className="flex items-center flex-wrap gap-y-1">
                Email
              </Label>
              <Input
                id="cd-email"
                type="email"
                value={details.email ?? ''}
                onChange={(e) => setField('email', e.target.value)}
                placeholder="accounts@acme.com"
                readOnly={isFinanceConnected}
                disabled={isFinanceConnected}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cd-website" className="flex items-center flex-wrap gap-y-1">
                Website
              </Label>
              <Input
                id="cd-website"
                type="url"
                value={details.website ?? ''}
                onChange={(e) => setField('website', e.target.value)}
                placeholder="https://www.acme.com"
                readOnly={isFinanceConnected}
                disabled={isFinanceConnected}
              />
            </div>
          </div>
        </div>

        <Separator />

        {/* ── Registered Address ───────────────────────────────────────── */}
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <MapPin className="h-4 w-4 text-muted-foreground" />
            <h3 className="text-sm font-semibold">Registered Address</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2 space-y-2">
              <Label htmlFor="cd-addr1" className="flex items-center flex-wrap gap-y-1">
                Address Line 1
              </Label>
              <Input
                id="cd-addr1"
                value={details.registeredAddress?.line1 ?? ''}
                onChange={(e) => setAddressField('line1', e.target.value)}
                placeholder="123 Business Park"
                readOnly={isFinanceConnected}
                disabled={isFinanceConnected}
              />
            </div>
            <div className="sm:col-span-2 space-y-2">
              <Label htmlFor="cd-addr2" className="flex items-center flex-wrap gap-y-1">
                Address Line 2
              </Label>
              <Input
                id="cd-addr2"
                value={details.registeredAddress?.line2 ?? ''}
                onChange={(e) => setAddressField('line2', e.target.value)}
                placeholder="Suite 4"
                readOnly={isFinanceConnected}
                disabled={isFinanceConnected}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cd-city" className="flex items-center flex-wrap gap-y-1">
                City
              </Label>
              <Input
                id="cd-city"
                value={details.registeredAddress?.city ?? ''}
                onChange={(e) => setAddressField('city', e.target.value)}
                placeholder="London"
                readOnly={isFinanceConnected}
                disabled={isFinanceConnected}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cd-county" className="flex items-center flex-wrap gap-y-1">
                County
              </Label>
              <Input
                id="cd-county"
                value={details.registeredAddress?.county ?? ''}
                onChange={(e) => setAddressField('county', e.target.value)}
                placeholder="Greater London"
                readOnly={isFinanceConnected}
                disabled={isFinanceConnected}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cd-postcode" className="flex items-center flex-wrap gap-y-1">
                Postcode
              </Label>
              <Input
                id="cd-postcode"
                value={details.registeredAddress?.postcode ?? ''}
                onChange={(e) => setAddressField('postcode', e.target.value)}
                placeholder="EC1A 1BB"
                readOnly={isFinanceConnected}
                disabled={isFinanceConnected}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cd-country" className="flex items-center flex-wrap gap-y-1">
                Country
              </Label>
              <Input
                id="cd-country"
                value={details.registeredAddress?.country ?? ''}
                onChange={(e) => setAddressField('country', e.target.value)}
                placeholder="United Kingdom"
                readOnly={isFinanceConnected}
                disabled={isFinanceConnected}
              />
            </div>
          </div>
        </div>

        <Separator />

        {/* ── Bank Details ─────────────────────────────────────────────── */}
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Landmark className="h-4 w-4 text-muted-foreground" />
            <h3 className="text-sm font-semibold">Bank Details</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="cd-bankname" className="flex items-center flex-wrap gap-y-1">
                Bank Name
              </Label>
              <Input
                id="cd-bankname"
                value={details.bankDetails?.bankName ?? ''}
                onChange={(e) => setBankField('bankName', e.target.value)}
                placeholder="Barclays"
                readOnly={isFinanceConnected}
                disabled={isFinanceConnected}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cd-accountname" className="flex items-center flex-wrap gap-y-1">
                Account Name
              </Label>
              <Input
                id="cd-accountname"
                value={details.bankDetails?.accountName ?? ''}
                onChange={(e) => setBankField('accountName', e.target.value)}
                placeholder="Acme Ltd"
                readOnly={isFinanceConnected}
                disabled={isFinanceConnected}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cd-sortcode" className="flex items-center flex-wrap gap-y-1">
                Sort Code
              </Label>
              <Input
                id="cd-sortcode"
                value={details.bankDetails?.sortCode ?? ''}
                onChange={(e) => setBankField('sortCode', e.target.value)}
                placeholder="20-00-00"
                readOnly={isFinanceConnected}
                disabled={isFinanceConnected}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cd-accountnumber" className="flex items-center flex-wrap gap-y-1">
                Account Number
              </Label>
              <Input
                id="cd-accountnumber"
                value={details.bankDetails?.accountNumber ?? ''}
                onChange={(e) => setBankField('accountNumber', e.target.value)}
                placeholder="12345678"
                readOnly={isFinanceConnected}
                disabled={isFinanceConnected}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cd-iban" className="flex items-center flex-wrap gap-y-1">
                IBAN
              </Label>
              <Input
                id="cd-iban"
                value={details.bankDetails?.iban ?? ''}
                onChange={(e) => setBankField('iban', e.target.value)}
                placeholder="GB29 NWBK 6016 1331 9268 19"
                readOnly={isFinanceConnected}
                disabled={isFinanceConnected}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cd-swift" className="flex items-center flex-wrap gap-y-1">
                SWIFT / BIC
              </Label>
              <Input
                id="cd-swift"
                value={details.bankDetails?.swiftBic ?? ''}
                onChange={(e) => setBankField('swiftBic', e.target.value)}
                placeholder="BARCGB22"
                readOnly={isFinanceConnected}
                disabled={isFinanceConnected}
              />
            </div>
          </div>
        </div>

        {/* Discard link for company details only (finance-connected users can't edit anyway) */}
        {!isFinanceConnected && hasUnsavedChanges && (
          <div className="flex justify-end pt-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { discardChanges(); toast.info('Changes discarded.'); }}
              disabled={isSaving}
              className="text-muted-foreground"
            >
              <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
              Discard changes
            </Button>
          </div>
        )}

      </div>
    </AccordionSection>
  );
  }
);


// ============================================================================
// Reporting & Calculations Section
// ============================================================================

const ReportingCalculationsSection = forwardRef<SectionHandle>(function ReportingCalculationsSection(_, ref) {
  const { isDemoMode } = useDataMode();
  const { authenticatedUser } = useAuth();
  const changedBy = isDemoMode
    ? 'Demo User'
    : authenticatedUser?.name ?? authenticatedUser?.email ?? 'Unknown User';

  const {
    settings,
    hasUnsavedChanges,
    isSaving,
    isLoading,
    updateSetting,
    saveSettings,
    resetToDefaults,
  } = useFinanceSettings();

  const save = useCallback(async () => {
    await saveSettings();
    appendAuditEntry({
      changedBy,
      category: 'Settings',
      action: 'Updated',
      entityName: 'Reporting & Calculations',
      newValue: `GP Method: ${GP_METHOD_LABELS[settings.gpCalculationMethod]}, Revenue Display: ${REVENUE_DISPLAY_MODES.find((m) => m.value === settings.revenueDisplayMode)?.label ?? settings.revenueDisplayMode}`,
      detail: 'Updated via Settings > General > Reporting & Calculations',
    });
  }, [saveSettings, changedBy, settings]);

  useImperativeHandle(ref, () => ({ save }), [save]);

  const handleReset = () => {
    resetToDefaults();
    toast.info('Settings reset to defaults.');
  };

  return (
    <div className="space-y-6">

      {/* ── GP Calculation Method ── */}
      <AccordionSection
        icon={<TrendingUp className="h-5 w-5 text-primary" />}
        title="GP Calculation Method"
        description="Choose how Gross Profit and Gross Margin % are calculated across all Financials tab cards, charts, and tables. This setting applies to every project company-wide."
      >
        <div className="space-y-6">
          <div className={`space-y-3${isLoading ? ' opacity-50 pointer-events-none' : ''}`}>
            {GP_METHODS.map((method) => {
              const isSelected = settings.gpCalculationMethod === method;
              return (
                <div
                  key={method}
                  onClick={() => updateSetting('gpCalculationMethod', method)}
                  className={`flex items-start gap-3 rounded-lg border p-4 cursor-pointer transition-colors ${
                    isSelected
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-muted-foreground/40'
                  }`}
                  role="radio"
                  aria-checked={isSelected}
                >
                  <div
                    className={`mt-0.5 h-4 w-4 shrink-0 rounded-full border-2 flex items-center justify-center ${
                      isSelected ? 'border-primary' : 'border-muted-foreground/40'
                    }`}
                  >
                    {isSelected && <div className="h-2 w-2 rounded-full bg-primary" />}
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-medium leading-none">
                      {GP_METHOD_LABELS[method]}
                      {method === 'simple' && (
                        <span className="ml-2 text-xs text-muted-foreground font-normal">(default)</span>
                      )}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {GP_METHOD_DESCRIPTIONS[method]}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          <Separator />
          <div className="flex justify-end">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleReset}
              className="gap-1.5 text-muted-foreground"
            >
              <RotateCcw className="h-3.5 w-3.5" />
              Reset to defaults
            </Button>
          </div>
        </div>
      </AccordionSection>

      {/* ── Revenue Display in P&L Report ── */}
      <AccordionSection
        icon={<FileBarChart className="h-5 w-5 text-primary" />}
        title="Revenue Display in P&L Report"
        description="Choose which revenue rows appear in the Project P&L Report. This setting applies company-wide to all P&L Report views."
      >
        <div className="space-y-6">
          <div className={`space-y-3${isLoading ? ' opacity-50 pointer-events-none' : ''}`}>
            {REVENUE_DISPLAY_MODES.map((mode) => {
              const isSelected = settings.revenueDisplayMode === mode.value;
              return (
                <div
                  key={mode.value}
                  onClick={() => updateSetting('revenueDisplayMode', mode.value)}
                  className={`flex items-start gap-3 rounded-lg border p-4 cursor-pointer transition-colors ${
                    isSelected
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-muted-foreground/40'
                  }`}
                  role="radio"
                  aria-checked={isSelected}
                >
                  <div
                    className={`mt-0.5 h-4 w-4 shrink-0 rounded-full border-2 flex items-center justify-center ${
                      isSelected ? 'border-primary' : 'border-muted-foreground/40'
                    }`}
                  >
                    {isSelected && <div className="h-2 w-2 rounded-full bg-primary" />}
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-medium leading-none">
                      {mode.label}
                      {mode.value === 'both' && (
                        <span className="ml-2 text-xs text-muted-foreground font-normal">(default)</span>
                      )}
                    </p>
                    <p className="text-xs text-muted-foreground">{mode.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </AccordionSection>

    </div>
  );
});

// ============================================================================
// Dashboard Targets Section
// ============================================================================

const DashboardTargetsSection = forwardRef<SectionHandle>(function DashboardTargetsSection(_, ref) {
  const { isDemoMode } = useDataMode();
  const { authenticatedUser } = useAuth();
  const changedBy = isDemoMode
    ? 'Demo User'
    : authenticatedUser?.name ?? authenticatedUser?.email ?? 'Unknown User';

  const {
    settings,
    hasUnsavedChanges: hasFinanceChanges,
    isSaving: isSavingFinance,
    isLoading,
    updateSetting,
    saveSettings,
    resetToDefaults,
  } = useFinanceSettings();

  const {
    formula: utilisationFormula,
    hasUnsavedChanges: hasUnsavedUtilisationChanges,
    isSaving: isSavingUtilisation,
    updateFormula: setUtilisationFormula,
    saveFormula: saveUtilisationFormulaApi,
  } = useUtilisationFormula();

  const utilisationFormulaOptions: { value: UIFormulaType; label: string; description: string }[] = [
    {
      value: 'actual_vs_budget',
      label: 'Actual vs Budgeted Hours',
      description: 'Actual hours ÷ Budgeted hours × 100. The most common formula — measures how much of the hours budget has been consumed.',
    },
    {
      value: 'actual_vs_allocated',
      label: 'Actual vs Allocated Hours',
      description: 'Actual hours ÷ Allocated hours × 100. Measures how much of the allocated (scheduled) capacity has been used.',
    },
    {
      value: 'billable_vs_capacity',
      label: 'Billable Actuals vs Available Capacity',
      description: 'Billable actual hours ÷ Available capacity hours × 100. Used when tracking billable efficiency against total available time.',
    },
  ];

  const hasAnyChanges = hasFinanceChanges || hasUnsavedUtilisationChanges;
  const isAnySaving = isSavingFinance || isSavingUtilisation;

  const save = useCallback(async () => {
    const promises: Promise<void>[] = [];
    if (hasFinanceChanges) {
      promises.push(
        saveSettings().then(() => {
          appendAuditEntry({
            changedBy,
            category: 'Settings',
            action: 'Updated',
            entityName: 'Dashboard Targets',
            detail: 'Updated via Settings > General > Dashboard Targets',
          });
        })
      );
    }
    if (hasUnsavedUtilisationChanges) {
      promises.push(
        saveUtilisationFormulaApi().then(() => {
          appendAuditEntry({
            changedBy,
            category: 'Settings',
            action: 'Updated',
            entityName: 'Utilisation Rate Formula',
            detail: 'Updated via Settings > General > Dashboard Targets',
          });
        })
      );
    }
    await Promise.all(promises);
  }, [hasFinanceChanges, hasUnsavedUtilisationChanges, saveSettings, saveUtilisationFormulaApi, changedBy]);

  useImperativeHandle(ref, () => ({ save }), [save]);

  const handleResetTargets = () => {
    resetToDefaults();
    toast.info('Dashboard targets reset to defaults.');
  };

  return (
    <div className="space-y-6">

      {/* ── Dashboard Thresholds & Targets ── */}
      <AccordionSection
        icon={<LayoutDashboard className="h-5 w-5 text-primary" />}
        title="Dashboard Reference Lines"
        description="Configure firm-wide benchmark percentages that render as dashed reference lines on the Executive Dashboard. Leave a field blank to hide the corresponding line."
      >
        <div className="space-y-6">
          <div className={`grid grid-cols-1 sm:grid-cols-2 gap-4${isLoading ? ' opacity-50 pointer-events-none' : ''}`}>

            {/* Portfolio Target Margin % */}
            <div className="rounded-lg border border-border p-4 space-y-3">
              <div className="space-y-1">
                <p className="text-sm font-semibold">Portfolio Target Margin</p>
                <p className="text-xs text-muted-foreground">
                  Renders a dashed target line on the Portfolio Profitability chart.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Input
                  id="fp-portfolioMargin"
                  type="number"
                  min={0}
                  max={100}
                  step={1}
                  placeholder="e.g. 25"
                  value={settings.portfolioTargetMarginPercent ?? ''}
                  onChange={(e) => {
                    const raw = e.target.value;
                    updateSetting(
                      'portfolioTargetMarginPercent',
                      raw === '' ? null : Math.min(100, Math.max(0, Number(raw))),
                    );
                  }}
                  className="w-28"
                />
                <span className="text-sm text-muted-foreground">%</span>
                {settings.portfolioTargetMarginPercent != null && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-xs text-muted-foreground h-7 px-2"
                    onClick={() => updateSetting('portfolioTargetMarginPercent', null)}
                  >
                    Clear
                  </Button>
                )}
              </div>
            </div>

            {/* Client Concentration Threshold % */}
            <div className="rounded-lg border border-border p-4 space-y-3">
              <div className="space-y-1">
                <p className="text-sm font-semibold">Client Concentration Threshold</p>
                <p className="text-xs text-muted-foreground">
                  Flags a warning on the Client Concentration Risk widget when a single client exceeds this share.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Input
                  id="fp-concentration"
                  type="number"
                  min={0}
                  max={100}
                  step={1}
                  placeholder="e.g. 30"
                  value={settings.concentrationThresholdPercent ?? ''}
                  onChange={(e) => {
                    const raw = e.target.value;
                    updateSetting(
                      'concentrationThresholdPercent',
                      raw === '' ? null : Math.min(100, Math.max(0, Number(raw))),
                    );
                  }}
                  className="w-28"
                />
                <span className="text-sm text-muted-foreground">%</span>
                {settings.concentrationThresholdPercent != null && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-xs text-muted-foreground h-7 px-2"
                    onClick={() => updateSetting('concentrationThresholdPercent', null)}
                  >
                    Clear
                  </Button>
                )}
              </div>
            </div>

            {/* Target Utilisation % — left column of second row */}
            <div className="rounded-lg border border-border p-4 space-y-3">
              <div className="space-y-1">
                <p className="text-sm font-semibold">Target Utilisation</p>
                <p className="text-xs text-muted-foreground">
                  Renders a dashed target line on the Utilisation Snapshot gauge and department bars.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Input
                  id="fp-utilisation"
                  type="number"
                  min={0}
                  max={100}
                  step={1}
                  placeholder="e.g. 75"
                  value={settings.targetUtilisationPercent ?? ''}
                  onChange={(e) => {
                    const raw = e.target.value;
                    updateSetting(
                      'targetUtilisationPercent',
                      raw === '' ? null : Math.min(100, Math.max(0, Number(raw))),
                    );
                  }}
                  className="w-28"
                />
                <span className="text-sm text-muted-foreground">%</span>
                {settings.targetUtilisationPercent != null && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-xs text-muted-foreground h-7 px-2"
                    onClick={() => updateSetting('targetUtilisationPercent', null)}
                  >
                    Clear
                  </Button>
                )}
              </div>
            </div>

          </div>

          <Separator />
          <div className="flex justify-end">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleResetTargets}
              className="gap-1.5 text-muted-foreground"
            >
              <RotateCcw className="h-3.5 w-3.5" />
              Reset to defaults
            </Button>
          </div>
        </div>
      </AccordionSection>

      {/* ── Utilisation Rate Formula ── */}
      <AccordionSection
        icon={<BarChart2 className="h-5 w-5 text-primary" />}
        title="Utilisation Rate Formula"
        description="Choose how Utilisation Rate is calculated across all projects and dashboard widgets company-wide."
      >
        <div className="space-y-6">
          <div className="space-y-3">
            {utilisationFormulaOptions.map((option) => (
              <div
                key={option.value}
                onClick={() => setUtilisationFormula(option.value)}
                className={`flex items-start gap-3 rounded-lg border p-4 cursor-pointer transition-colors ${
                  utilisationFormula === option.value
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-muted-foreground/40'
                }`}
                role="radio"
                aria-checked={utilisationFormula === option.value}
              >
                <div className={`mt-0.5 h-4 w-4 shrink-0 rounded-full border-2 flex items-center justify-center ${
                  utilisationFormula === option.value
                    ? 'border-primary'
                    : 'border-muted-foreground/40'
                }`}>
                  {utilisationFormula === option.value && (
                    <div className="h-2 w-2 rounded-full bg-primary" />
                  )}
                </div>
                <div className="space-y-1">
                  <p className="text-sm font-medium leading-none">{option.label}</p>
                  <p className="text-xs text-muted-foreground">{option.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </AccordionSection>

    </div>
  );
});

// ============================================================================
// Audit Trail Settings Section
// ============================================================================

type AuditRetentionPeriod = 'unlimited' | '1year' | '2years' | '3years' | '5years' | '7years';

interface AuditTrailState {
  enabled: boolean;
  retentionPeriod: AuditRetentionPeriod;
  auditProjectChanges: boolean;
  auditFinancialChanges: boolean;
  auditSettingsChanges: boolean;
  auditUserPermissionChanges: boolean;
  auditBillingInvoicing: boolean;
}

const AUDIT_DEFAULT_STATE: AuditTrailState = {
  enabled: false,
  retentionPeriod: 'unlimited',
  auditProjectChanges: true,
  auditFinancialChanges: true,
  auditSettingsChanges: true,
  auditUserPermissionChanges: true,
  auditBillingInvoicing: true,
};

const RETENTION_OPTIONS: { value: AuditRetentionPeriod; label: string }[] = [
  { value: 'unlimited', label: 'Unlimited (Phase 1 default)' },
  { value: '1year',     label: '1 year' },
  { value: '2years',    label: '2 years' },
  { value: '3years',    label: '3 years' },
  { value: '5years',    label: '5 years' },
  { value: '7years',    label: '7 years' },
];

const AUDIT_CATEGORIES: { key: keyof Omit<AuditTrailState, 'enabled' | 'retentionPeriod'>; label: string; description: string }[] = [
  {
    key: 'auditProjectChanges',
    label: 'Project Changes',
    description: 'Log project create, edit, status change, and delete events.',
  },
  {
    key: 'auditFinancialChanges',
    label: 'Financial Changes',
    description: 'Log budget changes, write-offs, and billing events.',
  },
  {
    key: 'auditSettingsChanges',
    label: 'Settings Changes',
    description: 'Log any change made within Settings.',
  },
  {
    key: 'auditUserPermissionChanges',
    label: 'User & Permission Changes',
    description: 'Log role assignments and user access changes.',
  },
  {
    key: 'auditBillingInvoicing',
    label: 'Billing & Invoicing',
    description: 'Log invoice creation, approval, and write-off events.',
  },
];

const AuditTrailSettingsSection = forwardRef<SectionHandle>(function AuditTrailSettingsSection(_, ref) {
  const [state, setState] = useState<AuditTrailState>(AUDIT_DEFAULT_STATE);
  const [savedState, setSavedState] = useState<AuditTrailState>(AUDIT_DEFAULT_STATE);
  const [isSaving, setIsSaving] = useState(false);

  const hasChanges =
    state.enabled !== savedState.enabled ||
    state.retentionPeriod !== savedState.retentionPeriod ||
    AUDIT_CATEGORIES.some((c) => state[c.key] !== savedState[c.key]);

  function updateField<K extends keyof AuditTrailState>(key: K, value: AuditTrailState[K]) {
    setState((prev) => ({ ...prev, [key]: value }));
  }

  const save = useCallback(async () => {
    setIsSaving(true);
    // TODO (GS-BE-4 → GS-BE-7): Replace this stub with a real API call:
    //   await fetch('/api/settings/audit', { method: 'PUT', body: JSON.stringify(state), credentials: 'include' });
    await new Promise((resolve) => setTimeout(resolve, 600));
    setSavedState(state);
    setIsSaving(false);
  }, [state]);

  useImperativeHandle(ref, () => ({ save }), [save]);

  const categoriesDisabled = !state.enabled;

  return (
    <div className="space-y-4">
      {/* Merged: Audit Logging + Retention Period */}
      <AccordionSection
        icon={<ShieldCheck className="h-5 w-5 text-primary" />}
        title="Audit Logging"
        description="Enable or disable the master audit log. When off, no events are recorded."
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Left card: Enable Audit Logging */}
          <div className="rounded-lg border border-border p-4 flex flex-col gap-2">
            <div className="flex items-start justify-between gap-2">
              <div className="space-y-1">
                <p className="text-sm font-semibold">Enable Audit Logging</p>
                <p className="text-xs text-muted-foreground">
                  Record a tamper-evident log of changes across the system.
                </p>
              </div>
              <Checkbox
                id="audit-master-toggle"
                checked={state.enabled}
                onCheckedChange={(checked) => updateField('enabled', checked === true)}
                className="mt-0.5 shrink-0"
              />
            </div>
          </div>

          {/* Right card: Retention Period */}
          <div className={`rounded-lg border border-border p-4 flex flex-col gap-2${categoriesDisabled ? ' opacity-50 pointer-events-none' : ''}`}>
            <p className="text-sm font-semibold">Retention Period</p>
            <p className="text-xs text-muted-foreground">
              How long audit log entries are retained before automatic deletion.
            </p>
            <Select
              value={state.retentionPeriod}
              onValueChange={(val) => updateField('retentionPeriod', val as AuditRetentionPeriod)}
              disabled={categoriesDisabled}
            >
              <SelectTrigger id="audit-retention" className="w-full">
                <SelectValue placeholder="Select retention period" />
              </SelectTrigger>
              <SelectContent>
                {RETENTION_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!categoriesDisabled && state.retentionPeriod !== 'unlimited' && (
              <p className="text-xs text-muted-foreground">
                Log entries older than the selected period will be automatically purged.
              </p>
            )}
          </div>
        </div>
      </AccordionSection>

      {/* Audit categories */}
      <AccordionSection
        icon={<BarChart2 className="h-5 w-5 text-primary" />}
        title="Audit Categories"
        description="Choose which types of events are recorded in the audit log. Enable audit logging above to configure these."
      >
        <div className={`grid grid-cols-1 sm:grid-cols-2 gap-4${categoriesDisabled ? ' opacity-50 pointer-events-none' : ''}`}>
          {AUDIT_CATEGORIES.map((category) => (
            <div key={category.key} className="rounded-lg border border-border p-4 flex flex-col gap-2">
              <div className="flex items-start justify-between gap-2">
                <p className="text-sm font-semibold">{category.label}</p>
                <Checkbox
                  id={`audit-${category.key}`}
                  checked={state[category.key] as boolean}
                  onCheckedChange={(checked) => updateField(category.key, checked === true)}
                  disabled={categoriesDisabled}
                  className="mt-0.5 shrink-0"
                />
              </div>
              <p className="text-xs text-muted-foreground">{category.description}</p>
            </div>
          ))}
        </div>
      </AccordionSection>
    </div>
  );
});

// ============================================================================
// Tab definitions
// ============================================================================

const GENERAL_TABS = [
  {
    value: 'organisation',
    label: 'Organisation',
    icon: Building2,
    description: 'Company information, address and bank details.',
  },
  {
    value: 'reporting',
    label: 'Reporting',
    icon: Calculator,
    description: 'GP calculation method, revenue display, and utilisation formula.',
  },
  {
    value: 'targets',
    label: 'Targets',
    icon: Target,
    description: 'Firm-wide benchmark targets for Executive Dashboard reference lines.',
  },
  {
    value: 'audit',
    label: 'Audit',
    icon: ShieldCheck,
    description: 'Audit log configuration and event retention settings.',
  },
] as const;

type GeneralTab = (typeof GENERAL_TABS)[number]['value'];

// ============================================================================
// Main Page
// ============================================================================

interface SettingsGeneralPageProps {
  defaultTab?: GeneralTab;
}

export function SettingsGeneralPage({ defaultTab }: SettingsGeneralPageProps = {}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isSaving, setIsSaving] = useState(false);
  const { canAccessSetting } = usePermissions();
  const { isWizardMode, registerSave } = useWizardMode();
  const { isDemoMode } = useDataMode();
  const { financeStatus: pageFinanceStatus } = useDashboardIntegrationStatus();

  // Fetch company details from Financials GraphQL via the backend endpoint
  const [financeImportData, setFinanceImportData] = useState<CompanyImportData | null>(null);
  useEffect(() => {
    if (isDemoMode) return;
    apiFetch<CompanyImportData>(`${getApiBaseUrl()}/integrations/finance/company-details`)
      .then((data) => {
        if (data) setFinanceImportData(data);
      })
      .catch(() => {
        // No finance integration connected — leave importData null
      });
  }, [isDemoMode]);

  const canConfigureAudit = canAccessSetting('configureAuditTrail');
  const visibleTabs = canConfigureAudit
    ? GENERAL_TABS
    : GENERAL_TABS.filter((t) => t.value !== 'audit');

  const rawTab = searchParams.get('tab') as GeneralTab | null;
  const validTabs: GeneralTab[] = visibleTabs.map((t) => t.value);
  const activeTab: GeneralTab = defaultTab ?? (rawTab && validTabs.includes(rawTab) ? rawTab : 'organisation');
  const isOrgTabReadOnly = pageFinanceStatus.isConnected && activeTab === 'organisation';

  const handleTabChange = (value: string) => {
    if (defaultTab) return; // tab switching disabled in wizard mode
    setSearchParams({ tab: value }, { replace: true });
  };

  // ── Refs to each section so we can call save() without state bubbling ─────
  const orgRef = useRef<SectionHandle>(null);
  const reportingRef = useRef<SectionHandle>(null);
  const targetsRef = useRef<SectionHandle>(null);
  const auditRef = useRef<SectionHandle>(null);

  const handleSaveAll = async () => {
    setIsSaving(true);
    try {
      await Promise.all([
        orgRef.current?.save(),
        reportingRef.current?.save(),
        targetsRef.current?.save(),
        auditRef.current?.save(),
      ]);
      if (!isWizardMode) toast.success('All settings saved successfully.');
    } catch {
      toast.error('Some settings failed to save. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Register save with wizard so the Next button triggers handleSaveAll.
  const wizardSaveRef = useRef(handleSaveAll);
  wizardSaveRef.current = handleSaveAll;
  useState(() => { if (isWizardMode) registerSave(async () => { await wizardSaveRef.current(); }); });

  const content = (
    <div className="space-y-6 p-6">

      {/* Page header — hidden in wizard (step wrapper provides context) */}
      {!defaultTab && (
        <div>
          <h1 className="text-xl font-semibold">General Settings</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {GENERAL_TABS.find((t) => t.value === activeTab)?.description}
          </p>
        </div>
      )}

      <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
        {/* Tab bar — hidden in wizard (single-tab mode) */}
        {!defaultTab && (
          <TabsList className="w-full justify-start">
            {visibleTabs.map(({ value, label, icon: Icon }) => (
              <TabsTrigger
                key={value}
                value={value}
                className="flex items-center gap-2 flex-none"
              >
                <Icon className="h-4 w-4" />
                {label}
              </TabsTrigger>
            ))}
          </TabsList>
        )}

        {/* Organisation tab */}
        <TabsContent value="organisation" className="space-y-4" data-tour="settings-general-company">
          <CompanyDetailsSection ref={orgRef} importData={financeImportData} />
        </TabsContent>

        {/* Reporting tab */}
        <TabsContent value="reporting" className="space-y-4" data-tour="settings-general-currency">
          <ReportingCalculationsSection ref={reportingRef} />
        </TabsContent>

        {/* Targets tab */}
        <TabsContent value="targets" className="space-y-4">
          <DashboardTargetsSection ref={targetsRef} />
        </TabsContent>

        {/* Audit tab — gated by configureAuditTrail permission */}
        {canConfigureAudit && (
          <TabsContent value="audit" className="space-y-4">
            <AuditTrailSettingsSection ref={auditRef} />
          </TabsContent>
        )}

      </Tabs>

      {/* Sticky footer save bar — hidden inside onboarding wizard and when finance controls the Organisation tab */}
      {!defaultTab && !isWizardMode && !isOrgTabReadOnly && (
        <div className="border-t bg-background px-6 py-3 flex items-center justify-end gap-2 shrink-0 mt-4">
          <Button variant="outline" size="sm" onClick={handleSaveAll} disabled={isSaving}>
            Cancel
          </Button>
          <Button size="sm" onClick={handleSaveAll} disabled={isSaving}>
            {isSaving ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Save
          </Button>
        </div>
      )}

    </div>
  );

  if (defaultTab) {
    return content;
  }

  return (
    <SettingsLayout currentPath={location.pathname} onNavigate={navigate}>
      <div className="flex flex-col flex-1 overflow-hidden">
        <div className="flex-1 overflow-y-auto">
          {content}
        </div>
      </div>
    </SettingsLayout>
  );
}

export default SettingsGeneralPage;
